import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { paragraphSmall } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import CheckboxStyles from '@stories/Components/Inputs/Checkbox/Checkbox.styles';
import DropdownStyles from '@stories/Components/Inputs/Dropdown/Dropdown.styles';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import DropdownFilterStyles from './DropdownFilter/DropdownFilter.styles';
const Wrapper = styled.section `
  margin: 0 0 ${fluid(28, 42)};

  &[data-variant='grey'] {
    position: relative;
    padding: 32px 0;

    &::before {
      content: '';
      height: 100%;
      width: 100vw;
      position: absolute;
      z-index: 1;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      background-color: ${brand.secondary.lightGrey};
    }
  }

  &[data-variant='blue'] {
    ${DropdownStyles.Container} {
      color: ${brand.white};

      option {
        color: ${brand.base.blue};
      }
    }
  }

  @media ${from(Device.Tablet)} {
    &[data-has-advanced='true'] {
      margin-bottom: calc(48px + ${fluid(28, 42)});
    }

    ${DropdownFilterStyles.FilterLabel} {
      white-space: nowrap;
    }

    ${DropdownFilterStyles.FilterItem},
    ${DropdownStyles.Container} {
      flex-basis: 33%;
    }

    &[data-many-filters='true'] {
      ${DropdownFilterStyles.FilterItem},
      ${DropdownStyles.Container} {
        max-width: 280px;
      }
    }

    &[data-many-filters='false'] {
      ${DropdownFilterStyles.FilterItem},
      ${DropdownStyles.Container} {
        max-width: 355px;
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${DropdownFilterStyles.FilterItem},
    ${DropdownStyles.Container} {
      flex-basis: 22%;
    }
  }
`;
const Container = styled.div `
  position: relative;
  z-index: 3;
  display: flex;
  gap: ${fluid(8, 46)};

  > ${ButtonStyles.Container} {
    height: ${fluid(45, 54)};

    ${ButtonStyles.IconWrapper} {
      width: 17px;
    }
  }

  ${DropdownStyles.Container} {
    margin: 0 0 0 auto;
  }

  @media ${until(Device.Tablet)} {
    > ${ButtonStyles.Container}, ${DropdownFilterStyles.FilterItem}, ${DropdownStyles.Container} {
      flex: 0 1 50%;
    }
  }

  @media ${from(Device.Tablet)} {
    align-items: flex-start;

    > ${ButtonStyles.Container} {
      display: none;
    }
  }
`;
const FiltersWrapper = styled.div `
  display: none;

  @media ${from(Device.Tablet)} {
    flex: 1;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    row-gap: ${fluid(8, 12)};

    ${Wrapper}[data-many-filters='false'] & {
      column-gap: ${fluid(8, 46)};
    }

    ${Wrapper}[data-many-filters='true'] & {
      column-gap: ${fluid(8, 16)};
    }
  }
`;
const Unset = styled.div `
  width: 100%;
  flex: 0 0 100%;
  display: flex;
  flex-wrap: wrap;
  gap: ${fluid(8, 16)} ${fluid(18, 24)};
  margin-top: ${fluid(12, 16)};
`;
const UnsetTitle = styled.span `
  ${fonts.apparat.semiBold};

  flex: 0 0 100%;
  border-top: 1px solid ${brand.secondary.blueGrey};
  padding-top: ${fluid(12, 16)};
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(26, 28)};
`;
const UnsetButton = styled.button `
  ${ButtonReset};
  ${transition('background-color')};
  ${fonts.apparat.light};

  border-radius: 5px;
  padding: 12px 14px 8px;
  color: ${brand.white};
  background: ${brand.primary.blue};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: ${fluid(14, 16)};
  text-transform: uppercase;

  > svg {
    display: block;
    width: 9px;
    transform: translateY(-2px);
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
    outline-style: solid;
  }

  &.focus-ring,
  &:hover {
    background-color: ${rgba(brand.primary.blue, 0.8)};
  }

  &[data-type='clear-all'] {
    background: ${brand.primary.orange};

    &.focus-ring,
    &:hover {
      background-color: ${rgba(brand.primary.orange, 0.8)};
    }
  }
`;
const Background = styled.div `
  ${transition('transform', 0.66)};

  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  z-index: 998;
  background-color: ${rgba(brand.base.blue, 0.75)};
  transform: translateX(calc(100% + 50px));
  cursor: zoom-out;

  &[data-active='true'] {
    transform: none;
  }

  @media ${from(Device.Tablet)} {
    display: none;
  }
`;
const Tray = styled.div `
  ${transition('transform', 0.66)};

  --shadowColour: rgba(0, 0, 0, 0.05);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(90vw, 375px);
  transform: translateX(calc(100% + 50px));
  padding: 24px 16px 16px;
  z-index: 999;
  background-color: ${brand.white};
  box-shadow: -0.1px 1px 1.1px var(--shadowColour), -0.2px 2.7px 3px -0.8px var(--shadowColour),
    -0.4px 6.3px 7.1px -1.7px var(--shadowColour), -0.9px 15px 16.9px -2.5px var(--shadowColour);

  &[data-active='true'] {
    transform: none;
  }

  > ${ButtonStyles.Container} {
    width: 100%;
    margin: auto 0 0;
    justify-content: center;
    text-align: center;
  }

  @media ${from(Device.Tablet)} {
    display: none;
  }
`;
const TrayTitle = styled.p `
  ${fonts.apparat.semiBold};

  font-size: ${fluid(22, 24)};
  line-height: ${fluid(32, 34)};
`;
const FilterList = styled.ul `
  ${scrollbars};

  margin: 42px -8px ${fluid(24, 32)} 0;
  flex: 1;
  overflow: auto;
  width: calc(100% + 8px);
  padding: 0 8px 0 0;
`;
const FilterItemCommonStyles = css `
  ${paragraphSmall};

  margin: 0;
  text-transform: uppercase;
  color: ${brand.primary.blue};
`;
const FilterItem = styled.li `
  padding: 0 0 ${fluid(16, 18)};
  margin: 0;
  border-bottom: 1px solid ${brand.primary.blueGrey};

  &[data-label] {
    font-weight: bold;
  }

  &[data-label]:not(:first-of-type) {
    margin-top: 32px;
  }

  &:not(:last-of-type) {
    margin-bottom: ${fluid(16, 18)};
  }

  ${CheckboxStyles.CheckboxText} {
    ${transition('transform')};
    ${FilterItemCommonStyles};

    padding: 0 32px 0 0;

    &::before,
    &::after {
      display: none;
    }

    /* Mobile click styles - button should mimic press */
    &:active {
      transform: translateY(1.5px);
    }
  }

  ${CheckboxStyles.Tick} {
    left: auto;
    right: 0;
    width: ${fluid(12, 14)};
  }

  ${CheckboxStyles.CheckboxInput}:checked + ${CheckboxStyles.CheckboxText} {
    ${fonts.apparat.semiBold};
  }
`;
const FilterButton = styled.button `
  ${ButtonReset};
  ${FilterItemCommonStyles};
  ${transition('transform')};

  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  > span {
    display: grid;
    place-content: center;
    background-color: ${brand.primary.blueGrey};
    height: 30px;
    width: 30px;
    border-radius: 4px;
  }

  /* Mobile click styles - button should mimic press */
  &:active {
    transform: translateY(1.5px);
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
    outline-style: solid;
  }
`;
const FilterTop = styled.div `
  display: flex;
  gap: ${fluid(12, 16)};
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
const BackButton = styled.button `
  ${ButtonReset};
  ${transition('transform')};
  ${fonts.apparat.semiBold};

  display: inline-flex;
  gap: ${fluid(8, 10)};
  align-items: center;
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(26, 28)};
  color: ${brand.primary.blue};

  > svg {
    display: block;
    width: ${fluid(12, 14)};
    color: var(--accentColor);
    transform: translateY(-1px);
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
    outline-style: solid;
  }

  /* Mobile click styles - button should mimic press */
  &:active {
    transform: translateY(1.5px);
  }
`;
const ClearButton = styled.button `
  ${ButtonReset}
  ${FilterItemCommonStyles};

  letter-spacing: 0.05em;
  display: inline-flex;
  gap: ${fluid(8, 10)};
  align-items: center;

  > svg {
    display: block;
    width: ${fluid(12, 14)};
    transform: translateY(-2px);
  }

  &:not(div) {
    /* Mobile click styles - button should mimic press */
    &:active {
      transform: translateY(1.5px);
    }

    &.focus-ring {
      outline: auto;
      outline-offset: 3px;
      outline-width: 2px;
      outline-style: solid;
    }
  }
`;
const AdvancedWrapper = styled(m.div) `
  position: relative;
  z-index: 2;
  min-width: 0;
  overflow: hidden;

  ${FiltersWrapper} {
    margin: ${fluid(48, 64)} 0 0;
  }
`;
const AdvancedButton = styled.button `
  ${ButtonReset};
  ${transition('background-color, color')};
  ${fonts.apparat.semiBold};

  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 2px 14px 0;
  font-size: 20px;
  width: 300px;
  height: 48px;
  border-radius: 0 0 20px 20px;
  background-color: ${brand.primary.blue};
  color: ${brand.white};
  border: 2px solid ${brand.primary.blue};
  border-top: 0;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 100%);
  z-index: 1;

  &.focus-ring,
  &:hover {
    background-color: ${brand.white};
    color: ${brand.primary.blue};
  }

  svg {
    display: block;
  }

  &[data-open='false'] {
    svg {
      width: 10px;
      transform: translateY(-5%) rotate(180deg);
    }
  }

  &[data-open='true'] {
    svg {
      width: 14px;
      transform: translateY(-10%);
    }
  }

  @media ${until(Device.Tablet)} {
    display: none;
  }
`;
const FiltersStyles = {
    BackButton,
    Background,
    ClearButton,
    Container,
    FilterButton,
    FilterItem,
    FilterList,
    FiltersWrapper,
    FilterTop,
    Tray,
    TrayTitle,
    Unset,
    UnsetButton,
    UnsetTitle,
    Wrapper,
    AdvancedWrapper,
    AdvancedButton,
};
export default FiltersStyles;
